import React, { useState, useEffect, useMemo } from "react";
import { apiUrl } from "../../globalVariables";
import styled, { useTheme, createGlobalStyle, ThemeProvider } from "styled-components";
import { useDarkMode } from "../../components/Theme/useDarkMode";
import { lightTheme, darkTheme } from "../../components/Theme/Themes";

// Styled-components for table and UI
const ThemedTable = styled.table`
  width: 100%;
  background: ${({ theme }) => theme.cardBackground} !important;
  color: ${({ theme }) => theme.text} !important;
`;
const ThemedTh = styled.th`
  color: ${({ theme }) => theme.text} !important;
  background: ${({ theme }) => theme.cardBackground} !important;
  position: sticky;
  top: 0;
  z-index: 1;
`;
const ThemedTd = styled.td`
  color: ${({ theme }) => theme.text} !important;
  background: ${({ theme }) => theme.cardBackground} !important;
`;
const ThemedTr = styled.tr`
  background: ${({ theme }) => theme.cardBackground} !important;
  color: ${({ theme }) => theme.text} !important;
`;
const ThemedPaginationInfo = styled.span`
  color: ${({ theme }) => theme.text} !important;
  font-weight: 500;
`;
const ThemedBadge = styled.span`
  background: ${({ theme }) => theme.accent || '#007bff'} !important;
  color: #fff !important;
  border-radius: 12px;
  padding: 0.25em 0.75em;
  font-size: 0.95em;
  margin-left: 0.5em;
  font-weight: 600;
`;

// Action Buttons Styled Components
const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const ExportButton = styled.button`
  background: ${({ theme }) => theme.accent || '#007bff'};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.accentDarker || '#0056b3'};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// Export Icon Component
const ExportIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" 
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="14,2 14,8 20,8" 
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="16" y1="13" x2="8" y2="13" 
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="16" y1="17" x2="8" y2="17" 
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Global style override for Bootstrap table, scoped to only the contacts table
const TableGlobalStyle = createGlobalStyle`
  .themed-contacts-table.table-striped tbody tr, 
  .themed-contacts-table.table-hover tbody tr:hover {
    background-color: ${({ theme }) => theme.cardBackground} !important;
    color: ${({ theme }) => theme.text} !important;
  }
  .themed-contacts-table thead th {
    color: ${({ theme }) => theme.text} !important;
    background: ${({ theme }) => theme.cardBackground} !important;
  }
`;

    const ContactsPage = () => {
    const [contacts, setContacts] = useState({});
    const [selectedCategory, setSelectedCategory] = useState(null);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(50); // Show 50 items per page
    const [searchTerm, setSearchTerm] = useState("");

    const [theme, , mountedComponent] = useDarkMode();
    const themeMode = theme === "light" ? lightTheme : darkTheme;

        useEffect(() => {
            const fetchContacts = async () => {
                try {
                const contactsData = await getContacts();
                setContacts(contactsData);
                // Set the first category as default if available
                const firstCategory = Object.keys(contactsData)[0];
                setSelectedCategory(firstCategory);
                    setLoading(false);
                } catch (error) {
                    console.error('Error fetching contacts:', error);
                    setError(error);
                    setLoading(false);
                }
        };
            fetchContacts();
        }, []);

    // Reset pagination when category changes
    useEffect(() => {
        setCurrentPage(1);
        setSearchTerm("");
    }, [selectedCategory]);

        const getContacts = async () => {
        const response = await fetch(`${apiUrl}/admin/contacts`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
        });
        const data = await response.json();
        
        if (!data.success) {
            console.error(data.reason);
                        throw new Error("Failed to get contacts from the database");
                    }
        
        return data.data;
    };

    // Utility function to format category names for display
    const formatCategoryName = (category) => {
        return category
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Utility function to get display value or fallback
    const getDisplayValue = (value) => {
        return value && value.trim() !== '' ? value : '-';
    };

    // CSV Export Functions
    const generateCSV = (contacts, categoryName) => {
        // Define CSV headers
        const headers = ['Email', 'First Name', 'Last Name', 'Phone'];
        
        // Convert contacts to CSV rows
        const csvRows = contacts.map(contact => [
            contact.email || '',
            contact.firstName || '',
            contact.lastName || '',
            contact.phone || ''
        ]);
        
        // Combine headers and data
        const allRows = [headers, ...csvRows];
        
        // Convert to CSV string
        const csvContent = allRows
            .map(row => row.map(field => `"${field.replace(/"/g, '""')}"`).join(','))
            .join('\n');
        
        return csvContent;
    };

    const downloadCSV = (csvContent, filename) => {
        // Create blob with CSV content
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        
        // Create download link
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up
        URL.revokeObjectURL(url);
    };

    const handleExport = async () => {
        if (!selectedCategory || !contacts[selectedCategory]) {
            console.warn('No category selected or no contacts available');
            return;
        }

        setIsExporting(true);
        try {
            // Get the contacts to export (respect current filters)
            let contactsToExport = contacts[selectedCategory];
            
            // Apply search filter if active
            if (searchTerm.trim()) {
                const searchLower = searchTerm.toLowerCase();
                contactsToExport = contactsToExport.filter(contact => 
                    getDisplayValue(contact.email).toLowerCase().includes(searchLower) ||
                    getDisplayValue(contact.firstName).toLowerCase().includes(searchLower) ||
                    getDisplayValue(contact.lastName).toLowerCase().includes(searchLower) ||
                    getDisplayValue(contact.phone).toLowerCase().includes(searchLower)
                );
            }

            // Generate CSV content
            const csvContent = generateCSV(contactsToExport, selectedCategory);
            
            // Create filename with timestamp
            const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
            const filename = `${formatCategoryName(selectedCategory)}_contacts_${timestamp}.csv`;
            
            // Download the file
            downloadCSV(csvContent, filename);
        } catch (error) {
            console.error('Export failed:', error);
            // Could add toast notification here in the future
        } finally {
            setIsExporting(false);
        }
    };

    // Memoized filtered and paginated contacts for performance
    const { currentContacts, totalPages, totalContacts } = useMemo(() => {
        if (!selectedCategory || !contacts[selectedCategory]) {
            return { currentContacts: [], totalPages: 0, totalContacts: 0 };
        }

        let filteredContacts = contacts[selectedCategory];

        // Apply search filter
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filteredContacts = filteredContacts.filter(contact => 
                getDisplayValue(contact.email).toLowerCase().includes(searchLower) ||
                getDisplayValue(contact.firstName).toLowerCase().includes(searchLower) ||
                getDisplayValue(contact.lastName).toLowerCase().includes(searchLower) ||
                getDisplayValue(contact.phone).toLowerCase().includes(searchLower)
            );
        }

        const totalContacts = filteredContacts.length;
        const totalPages = Math.ceil(totalContacts / itemsPerPage);
        
        // Apply pagination
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentContacts = filteredContacts.slice(startIndex, endIndex);

        return { currentContacts, totalPages, totalContacts };
    }, [selectedCategory, contacts, currentPage, searchTerm, itemsPerPage]);

    // Pagination controls
    const handlePageChange = (page) => {
        setCurrentPage(page);
        // Scroll to top of table
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        // Previous button
        if (currentPage > 1) {
            pages.push(
                <button
                    key="prev"
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                >
                    &laquo; Previous
                </button>
            );
        }

        // First page
        if (startPage > 1) {
            pages.push(
                <button
                    key="1"
                    className="page-link"
                    onClick={() => handlePageChange(1)}
                >
                    1
                </button>
            );
            if (startPage > 2) {
                pages.push(<span key="ellipsis1" className="page-link">...</span>);
            }
        }

        // Visible pages
        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    className={`page-link ${currentPage === i ? 'active' : ''}`}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </button>
            );
        }

        // Last page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(<span key="ellipsis2" className="page-link">...</span>);
            }
            pages.push(
                <button
                    key={totalPages}
                    className="page-link"
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </button>
            );
        }

        // Next button
        if (currentPage < totalPages) {
            pages.push(
                <button
                    key="next"
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                >
                    Next &raquo;
                </button>
            );
        }

        return (
            <nav aria-label="Contacts pagination">
                <ul className="pagination justify-content-center mb-0">
                    {pages.map((page, index) => (
                        <li key={index} className="page-item">
                            {page}
                        </li>
                    ))}
                </ul>
            </nav>
        );
    };

    if (!mountedComponent) return null;

    if (loading) {
        return (
            <div className="container-fluid p-4 ContactsPageContainer">
                <div className="d-flex justify-content-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid p-4 ContactsPageContainer">
                <div className="alert alert-danger" role="alert">
                    {error.message}
                </div>
            </div>
        );
    }

    return (
        <ThemeProvider theme={themeMode}>
            <div className="container-fluid p-4 ContactsPageContainer">
                <h1>Contacts</h1>
                <br/>
                <div className="row">
                    {/* Sidebar */}
                    <div className="col-md-3">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="mb-0">Categories</h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="list-group list-group-flush">
                                    {Object.keys(contacts).map((category) => (
                                        <button
                                            key={category}
                                            className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                                                selectedCategory === category ? 'active' : ''
                                            }`}
                                            onClick={() => setSelectedCategory(category)}
                                        >
                                            <span>{formatCategoryName(category)}</span>
                                            <span className="badge bg-secondary rounded-pill">
                                                {contacts[category]?.length || 0}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="col-md-9">
                        <div className="card">
                            <div className="card-header">
                                <div className="d-flex justify-content-between align-items-center">
                                    {/* Left Section - Category Name */}
                                    <h5 className="mb-0">
                                        {selectedCategory ? formatCategoryName(selectedCategory) : 'Select a category'}
                                        {totalContacts > 0 && (
                                            <ThemedBadge>
                                                {totalContacts} contacts
                                            </ThemedBadge>
                                        )}
                                    </h5>
                                    
                                    {/* Middle Section - Action Buttons */}
                                    {selectedCategory && totalContacts > 0 && (
                                        <ActionButtons>
                                            <ExportButton 
                                                onClick={handleExport}
                                                disabled={!selectedCategory || totalContacts === 0 || isExporting}
                                                title="Export contacts as CSV"
                                            >
                                                {isExporting ? (
                                                    <>
                                                        <div className="spinner-border spinner-border-sm me-1" role="status" />
                                                        Exporting...
                                                    </>
                                                ) : (
                                                    <>
                                                        <ExportIcon />
                                                        Export CSV
                                                    </>
                                                )}
                                            </ExportButton>
                                        </ActionButtons>
                                    )}
                                    
                                    {/* Right Section - Pagination Info */}
                                    {selectedCategory && (
                                        <div className="d-flex align-items-center gap-2">
                                            <ThemedPaginationInfo>
                                                Page {currentPage} of {totalPages}
                                            </ThemedPaginationInfo>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="card-body">
                                <TableGlobalStyle />
                                {selectedCategory ? (
                                    totalContacts > 0 ? (
                                        <>
                                            {/* Search Bar */}
                                            <div className="mb-3">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search contacts..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>

                                            {/* Table */}
                                            <div className="table-responsive" style={{maxHeight: '500px', overflowY: 'auto'}}>
                                                <ThemedTable className="table table-striped table-hover themed-contacts-table">
                                                    <thead>
                                                        <ThemedTr>
                                                            <ThemedTh>Email</ThemedTh>
                                                            <ThemedTh>First Name</ThemedTh>
                                                            <ThemedTh>Last Name</ThemedTh>
                                                            <ThemedTh>Phone</ThemedTh>
                                                        </ThemedTr>
                                                    </thead>
                                                    <tbody>
                                                        {currentContacts.map((contact, index) => (
                                                            <ThemedTr key={contact.id || index}>
                                                                <ThemedTd>{getDisplayValue(contact.email)}</ThemedTd>
                                                                <ThemedTd>{getDisplayValue(contact.firstName)}</ThemedTd>
                                                                <ThemedTd>{getDisplayValue(contact.lastName)}</ThemedTd>
                                                                <ThemedTd>{getDisplayValue(contact.phone)}</ThemedTd>
                                                            </ThemedTr>
                                                        ))}
                                                    </tbody>
                                                </ThemedTable>
                                            </div>

                                            {/* Pagination */}
                                            {renderPagination()}
                                        </>
                                    ) : (
                                        <div className="text-center py-4">
                                            <p className="text-muted">
                                                {searchTerm ? 'No contacts found matching your search.' : 'No contacts found in this category.'}
                                            </p>
                                        </div>
                                    )
                                ) : (
                                    <div className="text-center py-4">
                                        <p className="text-muted">Please select a category to view contacts.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ThemeProvider>
        );
    };

    export default ContactsPage;